<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useLaboratoryStore } from '../stores';
import { useCategoryStore } from '../stores/categoryStore';
import { useNotificationStore } from '../stores/notificationStore';
import type { Laboratory } from '../types';
import Modal from './Modal.vue';

const laboratoryStore = useLaboratoryStore();
const categoryStore = useCategoryStore();
const notificationStore = useNotificationStore();
const showModal = ref(false);

const newLaboratory = ref<Laboratory & { useWhatsappOrders?: boolean }>({
  Nombre: '',
  Ubicación: '',
  Contacto: '',
  Teléfono: '',
  WhatsApp: '',
  OrderUrl: '',
  useWhatsappOrders: false,
  descuentos: {}
});

const editingLab = ref<Laboratory | null>(null);
const processing = ref(false);

const handleNew = () => {
  editingLab.value = null;
  newLaboratory.value = {
    Nombre: '',
    Ubicación: '',
    Contacto: '',
    Teléfono: '',
    WhatsApp: '',
    OrderUrl: '',
    useWhatsappOrders: false,
    descuentos: {}
  };
  showModal.value = true;
};

const handleEdit = (lab: Laboratory) => {
  editingLab.value = lab;
  newLaboratory.value = { 
    ...lab,
    useWhatsappOrders: !lab.OrderUrl && !!lab.WhatsApp,
    descuentos: lab.descuentos || {}
  };
  showModal.value = true;
};

const handleDelete = async (lab: Laboratory) => {
  if (!lab.id) return;
  
  if (confirm('¿Está seguro de que desea eliminar este laboratorio?')) {
    try {
      processing.value = true;
      await laboratoryStore.deleteLaboratory(lab.id);
      notificationStore.success('Laboratorio eliminado correctamente');
    } catch (error) {
      notificationStore.error('Error al eliminar el laboratorio');
    } finally {
      processing.value = false;
    }
  }
};

const handleSubmit = async () => {
  try {
    processing.value = true;
    
    // Si useWhatsappOrders está activo, limpiar OrderUrl
    if (newLaboratory.value.useWhatsappOrders) {
      newLaboratory.value.OrderUrl = '';
    }

    const labData = {
      ...newLaboratory.value,
      WhatsApp: newLaboratory.value.WhatsApp?.replace(/\D/g, '') // Limpiar número de WhatsApp
    };
    
    // Eliminar la propiedad useWhatsappOrders antes de guardar
    const { useWhatsappOrders, ...labToSave } = labData;

    if (editingLab.value?.id) {
      await laboratoryStore.updateLaboratory(editingLab.value.id, labToSave);
      notificationStore.success('Laboratorio actualizado correctamente');
    } else {
      await laboratoryStore.addLaboratory(labToSave);
      notificationStore.success('Laboratorio agregado correctamente');
    }
    closeModal();
  } catch (error) {
    notificationStore.error('Error al guardar el laboratorio');
  } finally {
    processing.value = false;
  }
};

const closeModal = () => {
  showModal.value = false;
  editingLab.value = null;
  newLaboratory.value = {
    Nombre: '',
    Ubicación: '',
    Contacto: '',
    Teléfono: '',
    WhatsApp: '',
    OrderUrl: '',
    useWhatsappOrders: false,
    descuentos: {}
  };
};

const canUseWhatsapp = computed(() => {
  return !!newLaboratory.value.WhatsApp?.trim();
});

const handleWhatsappToggle = (checked: boolean) => {
  if (checked && !canUseWhatsapp.value) {
    notificationStore.warning('Debe ingresar un número de WhatsApp antes de activar esta opción');
    newLaboratory.value.useWhatsappOrders = false;
    return;
  }
  newLaboratory.value.useWhatsappOrders = checked;
};

const updateDiscount = (categoryId: string, value: string) => {
  const discount = parseFloat(value);
  if (isNaN(discount) || discount < 0 || discount > 100) {
    return;
  }
  newLaboratory.value.descuentos = {
    ...newLaboratory.value.descuentos,
    [categoryId]: discount
  };
};

// Watch WhatsApp number changes
watch(() => newLaboratory.value.WhatsApp, (newValue) => {
  if (!newValue?.trim() && newLaboratory.value.useWhatsappOrders) {
    newLaboratory.value.useWhatsappOrders = false;
    notificationStore.warning('Se ha desactivado el uso de WhatsApp para pedidos porque se eliminó el número');
  }
});
</script>

<template>
  <div>
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-2xl font-bold text-slate-800">Gestión de Laboratorios</h2>
      <button @click="handleNew" class="btn-primary">
        Nuevo Laboratorio
      </button>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div
        v-for="lab in laboratoryStore.laboratories"
        :key="lab.id"
        class="bg-white rounded-lg shadow-sm border border-slate-200 p-6"
      >
        <div class="flex justify-between items-start mb-4">
          <h3 class="text-lg font-semibold text-slate-800">{{ lab.Nombre }}</h3>
          <div class="flex space-x-2">
            <button
              @click="handleEdit(lab)"
              class="text-indigo-600 hover:text-indigo-800"
              :disabled="processing"
            >
              Editar
            </button>
            <button
              @click="handleDelete(lab)"
              class="text-red-600 hover:text-red-800"
              :disabled="processing"
            >
              Eliminar
            </button>
          </div>
        </div>
        <div class="space-y-2 text-sm text-slate-600">
          <p><span class="font-medium">Ubicación:</span> {{ lab.Ubicación }}</p>
          <p><span class="font-medium">Contacto:</span> {{ lab.Contacto }}</p>
          <p><span class="font-medium">Teléfono:</span> {{ lab.Teléfono }}</p>
          <p v-if="lab.WhatsApp"><span class="font-medium">WhatsApp:</span> {{ lab.WhatsApp }}</p>
          <p v-if="lab.OrderUrl"><span class="font-medium">URL de Pedidos:</span> {{ lab.OrderUrl }}</p>
          
          <!-- Descuentos -->
          <div v-if="lab.descuentos && Object.keys(lab.descuentos).length > 0">
            <p class="font-medium mt-4 mb-2">Descuentos por Categoría:</p>
            <ul class="space-y-1">
              <li v-for="[categoryId, discount] in Object.entries(lab.descuentos)" :key="categoryId">
                <span class="text-slate-700">{{ categoryStore.categories[categoryId] }}:</span>
                <span class="text-indigo-600 font-medium ml-1">{{ discount }}%</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <Modal
      :show="showModal"
      :title="editingLab ? 'Editar Laboratorio' : 'Nuevo Laboratorio'"
      @close="closeModal"
    >
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-2">Nombre</label>
            <input v-model="newLaboratory.Nombre" class="input-field" required />
          </div>

          <div>
            <label class="block text-sm font-medium text-slate-700 mb-2">Ubicación</label>
            <input v-model="newLaboratory.Ubicación" class="input-field" required />
          </div>

          <div>
            <label class="block text-sm font-medium text-slate-700 mb-2">Contacto</label>
            <input v-model="newLaboratory.Contacto" class="input-field" required />
          </div>

          <div>
            <label class="block text-sm font-medium text-slate-700 mb-2">Teléfono</label>
            <input v-model="newLaboratory.Teléfono" class="input-field" required />
          </div>

          <div>
            <label class="block text-sm font-medium text-slate-700 mb-2">WhatsApp</label>
            <input 
              v-model="newLaboratory.WhatsApp" 
              class="input-field" 
              placeholder="Ej: 549115555555"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-slate-700 mb-2">URL de Pedidos</label>
            <input 
              v-model="newLaboratory.OrderUrl" 
              type="url"
              class="input-field" 
              placeholder="https://..."
              :disabled="newLaboratory.useWhatsappOrders"
            />
          </div>
        </div>

        <!-- WhatsApp Orders Checkbox -->
        <div class="flex items-center space-x-2">
          <input
            type="checkbox"
            :checked="newLaboratory.useWhatsappOrders"
            @change="handleWhatsappToggle($event.target.checked)"
            class="rounded text-indigo-600 focus:ring-indigo-500"
            :disabled="!canUseWhatsapp"
          />
          <label class="text-sm text-slate-700">
            Utilizar WhatsApp para pedidos
          </label>
        </div>
        <p v-if="!canUseWhatsapp" class="text-xs text-amber-600">
          Debe ingresar un número de WhatsApp para habilitar esta opción
        </p>

        <!-- Descuentos por Categoría -->
        <div>
          <h4 class="text-sm font-medium text-slate-700 mb-4">Descuentos por Categoría</h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div v-for="[categoryId, label] in Object.entries(categoryStore.categories)" :key="categoryId">
              <label class="block text-sm text-slate-600 mb-1">{{ label }}</label>
              <div class="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  :value="newLaboratory.descuentos?.[categoryId] || ''"
                  @input="e => updateDiscount(categoryId, (e.target as HTMLInputElement).value)"
                  class="input-field pr-8"
                  placeholder="0"
                />
                <span class="absolute right-3 top-2 text-slate-500">%</span>
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-end space-x-4">
          <button 
            type="button" 
            @click="closeModal" 
            class="px-4 py-2 text-slate-600 hover:text-slate-800"
            :disabled="processing"
          >
            Cancelar
          </button>
          <button 
            type="submit" 
            class="btn-primary"
            :disabled="processing"
          >
            {{ processing ? 'Guardando...' : (editingLab ? 'Guardar Cambios' : 'Agregar Laboratorio') }}
          </button>
        </div>
      </form>
    </Modal>
  </div>
</template>